import { USER_ROLES } from './constants';

/**
 * One place that decides who may edit or delete an order/challan.
 *
 * The rule is identical for sales and purchase:
 *
 *                    | ADMIN         | SUPER_ADMIN
 *   before challan   | edit + delete | edit + delete
 *   after challan    | neither       | edit SAME DAY only; delete any date
 *
 * "Same day" compares the challan's own date with today in IST. That one condition also
 * covers the separate rule that a BACK-DATED challan is editable by nobody: a back-dated
 * challan is not same-day, so it fails for every role including SUPER_ADMIN.
 *
 * This mirrors app/utils/challan_rbac.py on the server, which is the authority — these
 * helpers only decide what to show, never what is permitted.
 */

/**
 * Calendar-day key ("YYYY-MM-DD"). Date-only values are read literally rather than
 * parsed into a Date, so a timezone conversion cannot shift a record a day either way.
 */
export const toDateKey = (value) => {
  if (!value) return null;
  const raw = String(value);
  const isoDate = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDate) return isoDate[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * Today in IST — the business timezone the API judges "same day" against. A browser set
 * to any other zone must not compute a different today, or the UI offers an edit the
 * server refuses (or hides one it would allow).
 */
export const todayKeyInIST = () => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date()).reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});
    if (parts.year && parts.month && parts.day) {
      return `${parts.year}-${parts.month}-${parts.day}`;
    }
  } catch (e) {
    // Runtime without IANA timezone data — fall back to the local day.
  }
  return toDateKey(new Date());
};

export const isSameDayAsToday = (value) => {
  const key = toDateKey(value);
  return !!key && key === todayKeyInIST();
};

/** Before a challan exists this is ordinary write access; after, SUPER_ADMIN + same day. */
export const canEditChallanRecord = ({ userRole, challanGenerated, challanDate }) => {
  if (!challanGenerated) return true;
  if (userRole !== USER_ROLES.SUPER_ADMIN) return false;
  // A missing challan date cannot be shown to be today, so it stays locked.
  return isSameDayAsToday(challanDate);
};

/** Delete after a challan is SUPER_ADMIN only, but with no date restriction. */
export const canDeleteChallanRecord = ({ userRole, challanGenerated }) => {
  if (!challanGenerated) return true;
  return userRole === USER_ROLES.SUPER_ADMIN;
};

/** Why an edit is unavailable, for a disabled-button tooltip. '' when it is allowed. */
export const editDeniedReason = ({ userRole, challanGenerated, challanDate }) => {
  if (canEditChallanRecord({ userRole, challanGenerated, challanDate })) return '';
  if (userRole !== USER_ROLES.SUPER_ADMIN) {
    return 'Cannot be edited once the challan has been generated';
  }
  return 'A challaned order can only be edited on the day its challan was raised';
};

/** Why a delete is unavailable, for a disabled-button tooltip. '' when it is allowed. */
export const deleteDeniedReason = ({ userRole, challanGenerated }) => {
  if (canDeleteChallanRecord({ userRole, challanGenerated })) return '';
  return 'Only a Super Admin can delete this once its challan has been generated';
};
